/**
 * Runtime command contract for Coverage upsert/cancel.
 * Browser-safe. Bots parse again before authorization or writes.
 */
import { z } from 'zod';

import { COVERAGE_INSURANCE_TYPE_CODES } from '../constants';
import type { CoveragePriorityLabel, CoverageRelationship } from '../resources/coverage';

export const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const calendarDateSchema = z
  .string()
  .regex(CALENDAR_DATE_RE, 'Must be a calendar date (YYYY-MM-DD)');

export const coverageRelationshipSchema = z.enum(['self', 'spouse', 'child', 'other']);

export const coveragePrioritySchema = z.enum(['primary', 'secondary', 'tertiary']);

/** Maps UI/portal labels (Self, parent/guardian) onto canonical FHIR relationship codes. */
export function canonicalCoverageRelationship(raw?: string | null): CoverageRelationship {
  const value = raw?.trim().toLowerCase();
  if (value === 'spouse') return 'spouse';
  if (value === 'child' || value === 'parent' || value === 'parent/guardian' || value === 'guardian') {
    return 'child';
  }
  if (value === 'other') return 'other';
  return 'self';
}

export const coverageUpsertCommandSchema = z
  .object({
    action: z.literal('upsert'),
    patientId: z.string().min(1, 'patientId is required'),
    coverageId: z.string().min(1).optional(),
    priority: coveragePrioritySchema,
    payerOrgId: z.string().min(1, 'payerOrgId is required'),
    memberId: z.string().min(1, 'memberId is required'),
    groupNumber: z.string().optional(),
    planName: z.string().optional(),
    relationship: coverageRelationshipSchema,
    subscriberFirstName: z.string().optional(),
    subscriberLastName: z.string().optional(),
    subscriberDateOfBirth: calendarDateSchema.optional(),
    periodStart: calendarDateSchema.optional(),
    periodEnd: calendarDateSchema.optional(),
    insuranceType: z.enum(COVERAGE_INSURANCE_TYPE_CODES).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.relationship !== 'self') {
      if (!val.subscriberFirstName?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Subscriber first name is required when relationship is not self',
          path: ['subscriberFirstName'],
        });
      }
      if (!val.subscriberLastName?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Subscriber last name is required when relationship is not self',
          path: ['subscriberLastName'],
        });
      }
      if (!val.subscriberDateOfBirth) {
        ctx.addIssue({
          code: 'custom',
          message: 'Subscriber date of birth is required when relationship is not self',
          path: ['subscriberDateOfBirth'],
        });
      }
    }
    if (val.periodStart && val.periodEnd && val.periodEnd < val.periodStart) {
      ctx.addIssue({
        code: 'custom',
        message: 'periodEnd must be on or after periodStart',
        path: ['periodEnd'],
      });
    }
  });

export const coverageCancelCommandSchema = z.object({
  action: z.literal('cancel'),
  patientId: z.string().min(1, 'patientId is required'),
  coverageId: z.string().min(1, 'coverageId is required'),
  expectedVersionId: z.string().min(1, 'expectedVersionId is required'),
});

export const coverageCommandSchema = z.discriminatedUnion('action', [
  coverageUpsertCommandSchema,
  coverageCancelCommandSchema,
]);

export type CoverageUpsertCommand = z.infer<typeof coverageUpsertCommandSchema>;
export type CoverageCancelCommand = z.infer<typeof coverageCancelCommandSchema>;
export type CoverageCommand = z.infer<typeof coverageCommandSchema>;

export type { CoveragePriorityLabel, CoverageRelationship };

/** Build a parseable upsert command from portal/intake field bags (capitalized relationship, extra payerName). */
export function toCoverageUpsertCommand(input: {
  patientId: string;
  coverageId?: string;
  priority: CoveragePriorityLabel;
  payerOrgId: string;
  memberId: string;
  groupNumber?: string;
  planName?: string;
  relationship?: string;
  subscriberFirstName?: string;
  subscriberLastName?: string;
  subscriberDateOfBirth?: string;
  periodStart?: string;
  periodEnd?: string;
  insuranceType?: CoverageUpsertCommand['insuranceType'];
}): CoverageUpsertCommand {
  return coverageUpsertCommandSchema.parse({
    action: 'upsert',
    patientId: input.patientId,
    coverageId: input.coverageId,
    priority: input.priority,
    payerOrgId: input.payerOrgId,
    memberId: input.memberId,
    groupNumber: input.groupNumber?.trim() || undefined,
    planName: input.planName?.trim() || undefined,
    relationship: canonicalCoverageRelationship(input.relationship),
    subscriberFirstName: input.subscriberFirstName?.trim() || undefined,
    subscriberLastName: input.subscriberLastName?.trim() || undefined,
    subscriberDateOfBirth: input.subscriberDateOfBirth?.trim() || undefined,
    periodStart: input.periodStart?.trim() || undefined,
    periodEnd: input.periodEnd?.trim() || undefined,
    insuranceType: input.insuranceType,
  });
}
